// Relative imports, not the '@' alias: jest has no moduleNameMapper for '@'
// and jest.config.js may not be touched. See useCourseList.ts.
import ChessRestClient from '../../Api/ChessRestClient';
import {getLocalStorage} from '../../Utils/CommonUtils';
import {useMutation, UseMutationResult} from '@tanstack/react-query';

import {CourseJob} from './types';

async function authHeader() {
  const token = await getLocalStorage('access_token');
  return {Authorization: `Bearer ${token}`};
}

export type SignedUpload = {
  key: string;
  /** Null when the file is already in the bucket and needs no transfer. */
  put_url: string | null;
  already_uploaded?: boolean;
  uploaded_at?: string | null;
  expires_in?: number;
  content_type: string;
};

/** How much of each end of the file goes into the fingerprint. */
const FINGERPRINT_EDGE_BYTES = 8 * 1024 * 1024;

/**
 * A short, stable identifier for a file's contents.
 *
 * SHA-256 over the exact byte length plus the first and last 8 MB — not the
 * whole file. `crypto.subtle` has no streaming digest, so hashing two
 * gigabytes in a browser means either holding it all in memory or blocking
 * the page for the best part of a minute. Reading 16 MB is instant.
 *
 * That makes it a strong heuristic, not a proof: two files would have to
 * share an exact byte count and 16 MB of identical head and tail to collide.
 * The server re-checks the size against what S3 holds, and the screen shows
 * what it is reusing, so a wrong guess is visible rather than silent.
 *
 * Returns '' where WebCrypto is unavailable (any non-HTTPS origin), which
 * simply means this upload is not deduplicated.
 */
export async function fingerprintFile(file: File): Promise<string> {
  const subtle =
    typeof crypto !== 'undefined' ? (crypto as any).subtle : undefined;
  if (!subtle?.digest) return '';

  try {
    const head = file.slice(0, Math.min(FINGERPRINT_EDGE_BYTES, file.size));
    const tail = file.slice(Math.max(0, file.size - FINGERPRINT_EDGE_BYTES));
    const [headBytes, tailBytes] = await Promise.all([
      head.arrayBuffer(),
      tail.arrayBuffer(),
    ]);

    // The length is part of the input, so two files sharing both ends still
    // differ unless they are also byte-for-byte the same size.
    const label = new TextEncoder().encode(`${file.size}:`);
    const joined = new Uint8Array(
      label.length + headBytes.byteLength + tailBytes.byteLength,
    );
    joined.set(label, 0);
    joined.set(new Uint8Array(headBytes), label.length);
    joined.set(new Uint8Array(tailBytes), label.length + headBytes.byteLength);

    const digest = await subtle.digest('SHA-256', joined);
    return Array.from(new Uint8Array(digest))
      .map(b => b.toString(16).padStart(2, '0'))
      .join('');
  } catch {
    // Reading the file failed, or WebCrypto refused. Upload without dedupe.
    return '';
  }
}

/**
 * Ask the backend for a URL we may PUT one video to.
 *
 * When the file has been uploaded before, the reply carries the existing key
 * and no `put_url` — there is nothing to send.
 */
export async function signUpload(file: File): Promise<SignedUpload> {
  const response = await ChessRestClient.post(
    '/video_learning/uploads/sign/',
    {
      filename: file.name,
      content_type: file.type,
      size_bytes: file.size,
      fingerprint: await fingerprintFile(file),
    },
    {headers: await authHeader()},
  );
  return response.data as SignedUpload;
}

/**
 * PUT the bytes straight to S3.
 *
 * Deliberately a bare XMLHttpRequest rather than ChessRestClient. Two reasons,
 * and the first is not optional: ChessRestClient attaches our Authorization
 * header, and S3 rejects a presigned request that carries one — the signature
 * covers the header set, so an extra header is a mismatch, reported as a
 * permissions error that looks nothing like the actual cause. The second is
 * that XHR is the only thing that reports upload progress; `fetch` has no
 * equivalent, and a multi-gigabyte upload with no progress bar looks hung.
 *
 * `Content-Type` is sent only when the signature bound one, and must then
 * match exactly.
 */
export function uploadToS3(
  signed: SignedUpload,
  file: File,
  onProgress?: (fraction: number) => void,
  signal?: AbortSignal,
): Promise<void> {
  return new Promise((resolve, reject) => {
    const request = new XMLHttpRequest();
    request.open('PUT', signed.put_url, true);
    if (signed.content_type) {
      request.setRequestHeader('Content-Type', signed.content_type);
    }

    request.upload.onprogress = event => {
      if (onProgress && event.lengthComputable && event.total > 0) {
        onProgress(event.loaded / event.total);
      }
    };
    request.onload = () => {
      if (request.status >= 200 && request.status < 300) {
        onProgress?.(1);
        resolve();
      } else {
        // S3 answers with an XML error document; the code inside it is far
        // more useful than the bare status.
        const detail = (request.responseText || '').slice(0, 300);
        reject(new Error(`Upload failed (${request.status}). ${detail}`));
      }
    };
    request.onerror = () =>
      reject(new Error('Upload failed. Check the connection and try again.'));
    request.onabort = () => reject(new Error('Upload cancelled.'));

    signal?.addEventListener('abort', () => request.abort());
    request.send(file);
  });
}

/** Base URL for the bare-XHR calls, which bypass ChessRestClient. */
function apiBase(): string {
  const configured = (ChessRestClient.defaults.baseURL || '').replace(/\/$/, '');
  return configured;
}

/**
 * Upload through our own backend instead of straight to S3.
 *
 * The slower path — the bytes cross the network twice and occupy an API
 * worker for the whole transfer — but it needs no CORS rule on the bucket, so
 * it works on a deploy where nobody has touched S3 yet.
 *
 * Still a bare XHR, for the progress events `fetch` cannot give.
 */
export function uploadDirect(
  file: File,
  onProgress?: (fraction: number) => void,
  signal?: AbortSignal,
): Promise<string> {
  return new Promise(async (resolve, reject) => {
    const headers = await authHeader();
    const form = new FormData();
    form.append('file', file);

    const request = new XMLHttpRequest();
    request.open('POST', `${apiBase()}/video_learning/uploads/direct/`, true);
    request.setRequestHeader('Authorization', headers.Authorization);
    // No Content-Type header: the browser must set it, because only it knows
    // the multipart boundary. Setting it by hand produces a body the server
    // cannot parse.

    request.upload.onprogress = event => {
      if (onProgress && event.lengthComputable && event.total > 0) {
        onProgress(event.loaded / event.total);
      }
    };
    request.onload = () => {
      if (request.status >= 200 && request.status < 300) {
        try {
          const key = JSON.parse(request.responseText || '{}').key;
          if (!key) {
            reject(new Error('Upload succeeded but returned no key.'));
            return;
          }
          onProgress?.(1);
          resolve(key);
        } catch {
          reject(new Error('Upload returned a response we could not read.'));
        }
        return;
      }
      let detail = `Upload failed (${request.status}).`;
      try {
        detail = JSON.parse(request.responseText || '{}').error || detail;
      } catch {
        /* a non-JSON body, e.g. nginx's own 413 page */
      }
      if (request.status === 413) {
        detail =
          'The server rejected the file as too large. ' +
          "nginx's client_max_body_size needs raising on the API host.";
      }
      reject(new Error(detail));
    };
    request.onerror = () =>
      reject(new Error('Upload failed. Check the connection and try again.'));
    request.onabort = () => reject(new Error('Upload cancelled.'));
    signal?.addEventListener('abort', () => request.abort());

    request.send(form);
  });
}

/**
 * Put one video in the bucket and return its key, by whichever route works.
 *
 * Tries the presigned PUT first — it is the fast path and costs the API
 * nothing — and falls back to the proxied endpoint, but only when **no bytes
 * left the browser**. That condition is the whole point: a missing CORS rule
 * fails at the preflight, and a signing endpoint that is not deployed yet
 * 404s, so in both cases nothing has been sent and retrying the other way is
 * free. A failure partway through is a real network problem, and silently
 * re-sending two gigabytes over a connection that just dropped would be a
 * poor way to discover that.
 */
export type UploadResult = {
  key: string;
  /** True when the file was already in the bucket and nothing was sent. */
  reused: boolean;
  uploadedAt?: string | null;
};

export async function uploadVideo(
  file: File,
  onProgress?: (fraction: number) => void,
  signal?: AbortSignal,
): Promise<UploadResult> {
  let moved = false;
  const track = (fraction: number) => {
    if (fraction > 0) moved = true;
    onProgress?.(fraction);
  };

  try {
    const signed = await signUpload(file);

    // Already in the bucket. Sending it again would transfer gigabytes to
    // overwrite an identical object, and — because uploads are keyed by
    // content — would land on the same key anyway.
    if (signed.already_uploaded || !signed.put_url) {
      onProgress?.(1);
      return {key: signed.key, reused: true, uploadedAt: signed.uploaded_at};
    }

    await uploadToS3(signed, file, track, signal);
    return {key: signed.key, reused: false};
  } catch (error) {
    if (signal?.aborted || moved) throw error;
    onProgress?.(0);
    const key = await uploadDirect(file, onProgress, signal);
    return {key, reused: false};
  }
}

/**
 * One video in a course. Exactly one of `key` or `video_course_id`.
 *
 * `key` is a file uploaded through this screen. `video_course_id` is a
 * recording picked from the catalogue, used as it stands — it is sent by id
 * rather than by key on purpose: a recording's object lives under its own
 * category folder, which the ingest endpoint refuses as a key, because a key
 * stored against the upload folder would be rebuilt pointing at nothing.
 */
export type IngestPart = {
  key?: string;
  video_course_id?: number;
  title?: string;
  transcript?: string;
  duration_sec?: number;
  /** The masterclass an edited upload was cut from. Uploads only. */
  source_video_course_id?: number;
  /** The original named as free text, when the row is not known. */
  source_title?: string;
};

export type IngestPayload = {
  title: string;
  parts: IngestPart[];
  /** Rebuild a course that already exists for the first part. */
  force?: boolean;
};

export type IngestResponse = CourseJob & {
  title: string;
  part_count: number;
  /** Parts whose audio will be transcribed during generation. */
  transcripts_pending: number[];
  /** Parts whose supplied transcript had no timings and could not be used. */
  transcripts_unusable: number[];
};

export async function ingestCourse(payload: IngestPayload): Promise<IngestResponse> {
  const response = await ChessRestClient.post(
    '/video_learning/courses/ingest/',
    payload,
    {headers: await authHeader()},
  );
  return response.data as IngestResponse;
}

export function useIngestCourse(): UseMutationResult<IngestResponse, Error, IngestPayload> {
  return useMutation<IngestResponse, Error, IngestPayload>(ingestCourse);
}

/** The slice of an HTMLVideoElement this module needs. */
type VideoProbe = {
  duration?: number;
  preload?: string;
  src?: string;
  onloadedmetadata?: (() => void) | null;
  onerror?: (() => void) | null;
};

export type DurationDeps = {
  makeVideo: () => VideoProbe;
  objectUrl: (file: File) => string;
  releaseUrl: (url: string) => void;
};

/**
 * Read a video's duration in the browser, so the server does not have to probe
 * a multi-gigabyte file just to learn how long it is.
 *
 * Best-effort, and deliberately so. It resolves 0 — never rejects — for a
 * container the browser cannot open, for a still-loading stream that reports
 * Infinity, and anywhere there is no DOM at all. 0 means "unknown", and the
 * pipeline probes the file itself in that case, so a failed metadata read
 * costs a little server time rather than blocking a course.
 *
 * `deps` is injected so this is testable without a DOM; the default reaches
 * for the real one.
 */
export function readVideoDuration(
  file: File,
  deps?: Partial<DurationDeps>,
): Promise<number> {
  const makeVideo =
    deps?.makeVideo ??
    (() =>
      typeof document === 'undefined'
        ? null
        : (document.createElement('video') as VideoProbe));
  const objectUrl =
    deps?.objectUrl ??
    ((f: File) => (typeof URL === 'undefined' ? '' : URL.createObjectURL(f)));
  const releaseUrl =
    deps?.releaseUrl ??
    ((url: string) => {
      if (typeof URL !== 'undefined') URL.revokeObjectURL(url);
    });

  return new Promise(resolve => {
    let url = '';
    let settled = false;
    const done = (seconds: unknown) => {
      if (settled) return;
      settled = true;
      if (url) {
        try {
          releaseUrl(url);
        } catch {
          /* releasing a url we may never have made is not worth a failure */
        }
      }
      const value = Number(seconds);
      resolve(Number.isFinite(value) && value > 0 ? Math.round(value) : 0);
    };

    try {
      const video = makeVideo();
      if (!video) {
        done(0);
        return;
      }
      url = objectUrl(file);
      video.preload = 'metadata';
      video.onloadedmetadata = () => done(video.duration);
      video.onerror = () => done(0);
      video.src = url;
    } catch {
      done(0);
    }
  });
}
